/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ParseSourceSpan} from '../parse_util';

export class Message {
/**
 * @param {?} nodes message AST
 * @param {?} placeholders maps placeholder names to static content
 * @param {?} placeholderToMessage maps placeholder names to messages (used for nested ICU messages)
 * @param {?} meaning
 * @param {?} description
 */
constructor(
public nodes: Node[],
public placeholders: {[phName: string]: string},
public placeholderToMessage: {[phName: string]: Message},
public meaning: string,
public description: string) {}
}

function Message_tsickle_Closure_declarations() {
 /** @type {?} */
Message.prototype.nodes;
 /** @type {?} */
Message.prototype.placeholders;
 /** @type {?} */
Message.prototype.placeholderToMessage;
 /** @type {?} */
Message.prototype.meaning;
 /** @type {?} */
Message.prototype.description;
}


export interface Node {
  sourceSpan: ParseSourceSpan;
  visit(visitor: Visitor, context?: any): any;
}

export class Text implements Node {
/**
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(public value: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitText(this, context); }
}

function Text_tsickle_Closure_declarations() {
 /** @type {?} */
Text.prototype.value;
 /** @type {?} */
Text.prototype.sourceSpan;
}


// TODO(vicb): do we really need this node (vs an array) ?
export class Container implements Node {
/**
 * @param {?} children
 * @param {?} sourceSpan
 */
constructor(public children: Node[],
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitContainer(this, context); }
}

function Container_tsickle_Closure_declarations() {
 /** @type {?} */
Container.prototype.children;
 /** @type {?} */
Container.prototype.sourceSpan;
}


export class Icu implements Node {
public expressionPlaceholder: string;
/**
 * @param {?} expression
 * @param {?} type
 * @param {?} cases
 * @param {?} sourceSpan
 */
constructor(
public expression: string,
public type: string,
public cases: {[k: string]: Node},
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitIcu(this, context); }
}

function Icu_tsickle_Closure_declarations() {
 /** @type {?} */
Icu.prototype.expressionPlaceholder;
 /** @type {?} */
Icu.prototype.expression;
 /** @type {?} */
Icu.prototype.type;
 /** @type {?} */
Icu.prototype.cases;
 /** @type {?} */
Icu.prototype.sourceSpan;
}


export class TagPlaceholder implements Node {
/**
 * @param {?} tag
 * @param {?} attrs
 * @param {?} startName
 * @param {?} closeName
 * @param {?} children
 * @param {?} isVoid
 * @param {?} sourceSpan
 */
constructor(
public tag: string,
public attrs: {[k: string]: string},
public startName: string,
public closeName: string,
public children: Node[],
public isVoid: boolean,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitTagPlaceholder(this, context); }
}

function TagPlaceholder_tsickle_Closure_declarations() {
 /** @type {?} */
TagPlaceholder.prototype.tag;
 /** @type {?} */
TagPlaceholder.prototype.attrs;
 /** @type {?} */
TagPlaceholder.prototype.startName;
 /** @type {?} */
TagPlaceholder.prototype.closeName;
 /** @type {?} */
TagPlaceholder.prototype.children;
 /** @type {?} */
TagPlaceholder.prototype.isVoid;
 /** @type {?} */
TagPlaceholder.prototype.sourceSpan;
}


export class Placeholder implements Node {
/**
 * @param {?} value
 * @param {?} name
 * @param {?} sourceSpan
 */
constructor(public value: string,
public name: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitPlaceholder(this, context); }
}

function Placeholder_tsickle_Closure_declarations() {
 /** @type {?} */
Placeholder.prototype.value;
 /** @type {?} */
Placeholder.prototype.name;
 /** @type {?} */
Placeholder.prototype.sourceSpan;
}


export class IcuPlaceholder implements Node {
/**
 * @param {?} value
 * @param {?} name
 * @param {?} sourceSpan
 */
constructor(public value: Icu,
public name: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: Visitor, context?: any): any { return visitor.visitIcuPlaceholder(this, context); }
}

function IcuPlaceholder_tsickle_Closure_declarations() {
 /** @type {?} */
IcuPlaceholder.prototype.value;
 /** @type {?} */
IcuPlaceholder.prototype.name;
 /** @type {?} */
IcuPlaceholder.prototype.sourceSpan;
}


export interface Visitor {
  visitText(text: Text, context?: any): any;
  visitContainer(container: Container, context?: any): any;
  visitIcu(icu: Icu, context?: any): any;
  visitTagPlaceholder(ph: TagPlaceholder, context?: any): any;
  visitPlaceholder(ph: Placeholder, context?: any): any;
  visitIcuPlaceholder(ph: IcuPlaceholder, context?: any): any;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
